import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveEmployeeStoreContext } from './entityContext';
import {
  DEFAULT_DELIVERY_AGENT_ORDER_SOURCE,
  getDeliveryAgentOrderSources,
} from './menuChannels';
import { RESTAURANT_NAV_CLOSE, RESTAURANT_NAV_OPEN, RESTAURANT_NAV_TOGGLE } from './restaurantNav';
import useResponsiveTableLabels from './useResponsiveTableLabels';
import './css/Home.css';
import './css/ResponsiveDashboard.css';
import './css/Employee.css';

/** Delivery-agent employee shell — Orders tab + MYEATS / Restaurant (or JREATS) subnav only. */
const EmployeeDashboardLayout = ({
  children,
  deliveryOrderSource = DEFAULT_DELIVERY_AGENT_ORDER_SOURCE,
  deliveryOrderSources: deliveryOrderSourcesProp,
  onNavigateOrders,
  onSelectOrderSource,
  showOrderSourceSubnav = true,
}) => {
  const navigate = useNavigate();
  const mainRef = useRef(null);
  useResponsiveTableLabels(mainRef);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const toggleMobileNav = useCallback(() => setMobileNavOpen((open) => !open), []);

  useEffect(() => {
    document.documentElement.classList.add('has-restaurant-dashboard');
    return () => {
      document.documentElement.classList.remove('has-restaurant-dashboard');
      document.documentElement.classList.remove('restaurant-nav-open');
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('restaurant-nav-open', mobileNavOpen);
  }, [mobileNavOpen]);

  useEffect(() => {
    window.addEventListener(RESTAURANT_NAV_OPEN, openMobileNav);
    window.addEventListener(RESTAURANT_NAV_CLOSE, closeMobileNav);
    window.addEventListener(RESTAURANT_NAV_TOGGLE, toggleMobileNav);
    return () => {
      window.removeEventListener(RESTAURANT_NAV_OPEN, openMobileNav);
      window.removeEventListener(RESTAURANT_NAV_CLOSE, closeMobileNav);
      window.removeEventListener(RESTAURANT_NAV_TOGGLE, toggleMobileNav);
    };
  }, [openMobileNav, closeMobileNav, toggleMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMobileNav();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen, closeMobileNav]);

  useEffect(() => {
    closeMobileNav();
  }, [deliveryOrderSource, closeMobileNav]);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const isStore = resolveEmployeeStoreContext(user);
  const deliveryOrderSources = useMemo(() => {
    if (Array.isArray(deliveryOrderSourcesProp) && deliveryOrderSourcesProp.length > 0) {
      return deliveryOrderSourcesProp;
    }
    return getDeliveryAgentOrderSources(isStore);
  }, [deliveryOrderSourcesProp, isStore]);

  const goToOrders = (source = deliveryOrderSource) => {
    if (onNavigateOrders) {
      onNavigateOrders(source);
      return;
    }
    navigate('/business/user', { state: { deliveryOrderSource: source } });
  };

  const handleSourceClick = (key) => {
    if (onSelectOrderSource) {
      onSelectOrderSource(key);
      return;
    }
    goToOrders(key);
  };

  return (
    <div
      className={`restaurant-dashboard restaurant-dashboard--flush employee-dashboard${
        mobileNavOpen ? ' restaurant-dashboard--nav-open' : ''
      }`}
    >
      <button
        type="button"
        className="restaurant-dashboard-nav-backdrop"
        aria-label="Close navigation menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={closeMobileNav}
      />
      <aside
        id="restaurant-dashboard-sidebar"
        className="restaurant-dashboard-sidebar"
        aria-label="Employee navigation"
      >
        <button
          type="button"
          className="restaurant-dashboard-sidebar-item active"
          onClick={() => goToOrders()}
        >
          Orders
        </button>
        {showOrderSourceSubnav ? (
          <div
            className="restaurant-dashboard-subnav"
            role="group"
            aria-label="Order source"
          >
            {deliveryOrderSources.map((channel) => {
              const Icon = channel.icon;
              return (
                <button
                  key={channel.key}
                  type="button"
                  className={`restaurant-dashboard-subnav-item${
                    deliveryOrderSource === channel.key ? ' active' : ''
                  }`}
                  onClick={() => handleSourceClick(channel.key)}
                >
                  <Icon
                    className="restaurant-dashboard-subnav-icon"
                    aria-hidden="true"
                  />
                  <span>{channel.label}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </aside>
      <main
        ref={mainRef}
        className="restaurant-dashboard-main restaurant-dashboard-main--flush employee-dashboard-main rt-scope"
      >
        {children}
      </main>
    </div>
  );
};

export default EmployeeDashboardLayout;
